import React, { useState, useEffect, useCallback, useRef } from "react";
import MoodTracker from "./MoodTracker";
import DayNavigator from "../../components/DayNavigator";
import WeeklyLog from "./WeeklyLog";
import { syncLogToShopify } from "../../utils/syncLogToShopify";
import { launchConfetti } from "../../utils/confetti";
import { calculateStreak } from '../../utils/calculateStreak';

const meals = ["Breakfast", "Snack 1", "Lunch", "Snack 2", "Dinner"];

// Helper to parse a "YYYY-MM-DD" string as a local Date
const parseLocalDateString = (str) => {
  const [year, month, day] = str.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toDatetimeLocal = (isoString) => {
  if (!isoString || typeof isoString !== "string") return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date - tzOffset).toISOString().slice(0, 16);
};

const getDateStrFromDate = (date) => {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return local.toISOString().split("T")[0];
};

const DailyMoodLogger = ({ streak, setStreak, currentScreen }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayNumber, setDayNumber] = useState(1);
  const [openMeal, setOpenMeal] = useState(null);
  const [log, setLog] = useState(() => {
    try {
      const stored = localStorage.getItem("custom.hyman_hive_mood_log");
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error parsing mood log from localStorage", e);
      return {};
    }
  });

  // Add local state for saving log
  const [savingLog, setSavingLog] = useState(false);

  // Store startDate in state to avoid repeated localStorage access
  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem("custom.hyman_hive_mood_start_date");
  });

  // Fallback logic for startDate: if not set, use the date of the first log entry,
  // or the currently selected date if none exists.
  useEffect(() => {
    if (!startDate) {
      const firstLoggedDay = Object.values(log)[0];
      const firstDate = firstLoggedDay?.date;
      if (firstDate) {
        const dateStr = getDateStrFromDate(selectedDate);

        localStorage.setItem("custom.hyman_hive_mood_start_date", dateStr);
        setStartDate(dateStr);
        syncLogToShopify(
          "single_line_text_field",
          dateStr,
          "hyman_hive_mood_start_date"
        );
      }
    }
  }, [log, startDate, selectedDate]);

  const getDayNumberFromDate = (selectedDate) => {
    if (!startDate) return 1;
    const msPerDay = 1000 * 60 * 60 * 24;
    const start = parseLocalDateString(startDate);
    // Normalize selectedDate to local date (year, month, day)
    const selected = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    );
    return Math.floor((selected - start) / msPerDay) + 1;
  };

  useEffect(() => {
    if (selectedDate && startDate) {
      const newDayNumber = getDayNumberFromDate(selectedDate);
      setDayNumber(newDayNumber);
    }
  }, [selectedDate, startDate]);

  const updateMeal = useCallback(
    (mealName, updatedEntries) => {
      const dayKey = `Day ${dayNumber}`;
      const newLog = { ...log };

      const dateStr = getDateStrFromDate(selectedDate);

      if (!newLog[dayKey]) {
        newLog[dayKey] = {
          date: dateStr,
          entries: {},
        };
      }

      if (!newLog[dayKey].entries) {
        newLog[dayKey].entries = {};
      }

      newLog[dayKey].entries[mealName] = {
        Details: updatedEntries?.["Details"] || [],
        "Mood Before Meal": updatedEntries?.["Mood Before Meal"] || [],
        "Mood After Meal": updatedEntries?.["Mood After Meal"] || [],
        "Physical Symptoms": updatedEntries?.["Physical Symptoms"] || [],
        "Lifestyle Factors": updatedEntries?.["Lifestyle Factors"] || [],
        Timestamp: updatedEntries?.["Timestamp"] || [],
      };

      setLog(newLog);
      localStorage.setItem(
        "custom.hyman_hive_mood_log",
        JSON.stringify(newLog)
      );
    },
    [dayNumber, log, selectedDate]
  );

  const dayKey = `Day ${getDayNumberFromDate(selectedDate)}`;
  const dayLog = log[dayKey]?.entries || {};

  useEffect(() => {
    if (currentScreen === "mood") {
      setStreak(calculateStreak(log));
    }
  }, [log, currentScreen]);

  return (
    <div className="tw-flex tw-gap-6 tw-flex-start tw-justify-start tw-flex-col-reverse lg:tw-flex-row">
      <div className="tw-flex tw-mb-6 tw-w-full lg:tw-w-1/3 tw-flex-col tw-gap-6">
        <div className="tw-flex tw-flex-col tw-bg-white dark:tw-bg-black tw-rounded-2xl tw-shadow-sm tw-p-8 tw-text-center">
          <div className="tw-flex tw-justify-center tw-items-center tw-w-full tw-min-h-[200px]">
            {dayNumber < 8 ? (
              <h2 className="dark:tw-text-white tw-text-gray-500">
                Insights coming soon
              </h2>
            ) : (
              <h2 className="dark:tw-text-white tw-text-gray-500">
                Insight card coming soon
              </h2>
            )}
          </div>
        </div>

        <WeeklyLog
          currentDayNumber={dayNumber}
          startDate={startDate}
          logData={log}
        />
      </div>

      <div className="tw-w-full lg:tw-2/3 daily-log">
        <div className="tw-bg-white  dark:tw-bg-black tw-px-3 sm:tw-px-10  tw-py-4 lg:tw-py-8 tw-rounded-2xl tw-shadow-sm tw-w-full tw-h-full">
          <div className="welcome tw-border-b tw-pb-7 tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-justify-between dark:tw-border-gray-700">
            <DayNavigator
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              dayNumber={dayNumber}
              startDate={startDate}
            />
          </div>

          {meals.map((meal, index) => (
            <div
              key={meal}
              className={
                index === meals.length - 1
                  ? ""
                  : "tw-border-b hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 dark:tw-border-gray-700"
              }
            >
              <button
                className="tw-w-full tw-text-left tw-py-6 tw-flex tw-justify-between tw-px-3 lg:tw-px-4 tw-flex tw-gap-3 tw-items-center"
                onClick={() => setOpenMeal(openMeal === index ? null : index)}
              >
                <span
                  className={`tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded-full ${(() => {
                    const mealEntry = dayLog[meal];
                    const hasAnyValue =
                      mealEntry &&
                      Object.values(mealEntry).some((val) => {
                        if (Array.isArray(val)) return val.length > 0;
                        if (typeof val === "object")
                          return Object.values(val).some((v) => v?.length > 0);
                        return !!val;
                      });
                    return hasAnyValue
                      ? "tw-bg-green-500 tw-text-white dark:tw-text-black"
                      : "tw-bg-gray-300 tw-text-white dark:tw-text-black";
                  })()}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="tw-h-4 tw-w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
                <h3 className="tw-flex-1">{meal}</h3>

                <span className="tw-flex tw-flex-2 tw-items-center tw-gap-2">
                  {(() => {
                    const mealEntry = dayLog[meal];
                    return (
                      mealEntry?.Timestamp && (
                        <span className="tw-text-sm tw-text-gray-400 tw-ml-2">
                          {new Date(mealEntry.Timestamp).toLocaleTimeString(
                            [],
                            {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      )
                    );
                  })()}
                  <svg
                    className={`tw-h-5 tw-w-5 tw-transition-transform tw-text-gray-500 ${
                      openMeal === index ? "tw-rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </span>
              </button>

              <MoodTracker
                entries={dayLog[meal] || []}
                onChange={(updatedEntries) => updateMeal(meal, updatedEntries)}
                isVisible={openMeal === index}
              />

              {openMeal === index && (
                <div className="tw-relative tw-flex tw-justify-start tw-mb-8 tw-mx-4">
                  <button
                    id={`log-save-button-${index}`}
                    onClick={() => {
                      setSavingLog(true);

                      let currentTimestamp = dayLog[meal]?.Timestamp;

                      if (!currentTimestamp) {
                        const now = new Date();
                        currentTimestamp = now.toISOString();
                        updateMeal(meal, {
                          ...dayLog[meal],
                          Timestamp: currentTimestamp,
                        });
                      }

                      // Trigger GA4 custom event for mood log
                      gtag("event", "mood_log_saved", {
                        meal_type: meal.toLowerCase().replace(" ", "_"),
                        mood_before_count:
                          dayLog[meal]?.["Mood Before Meal"]?.length || 0,
                        mood_after_count:
                          dayLog[meal]?.["Mood After Meal"]?.length || 0,
                        symptoms_count:
                          dayLog[meal]?.["Physical Symptoms"]?.length || 0,
                        lifestyle_factors_count:
                          dayLog[meal]?.["Lifestyle Factors"]?.length || 0,
                        timestamp_logged: !!currentTimestamp,
                      });

                      const canvas = document.querySelector(
                        `#log-save-canvas-${index}`
                      );
                      const button = document.querySelector(
                        `#log-save-button-${index}`
                      );
                      if (canvas && button) launchConfetti(canvas, button);

                      syncLogToShopify("json", log, "hyman_hive_mood_log").then(
                        () => {
                          setTimeout(() => {
                            setSavingLog(false);
                            setOpenMeal(
                              index + 1 < meals.length ? index + 1 : null
                            );
                          }, 2000);
                        }
                      );
                    }}
                    className={`tw-px-6 tw-py-3 tw-rounded-full tw-shadow-sm ${
                      savingLog
                        ? "tw-bg-gray-500 tw-text-white"
                        : "tw-bg-black hover:tw-bg-gray-700 tw-text-white"
                    }`}
                  >
                    {savingLog ? (
                      <span className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-loader tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-w-4 tw-h-4 tw-animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      `Save ${meal.toLowerCase().replace(/ 1| 2/, "")} log`
                    )}
                  </button>

                  <div className="tw-mx-4 tw-flex tw-items-center tw-gap-2">
                    <input
                      type="datetime-local"
                      value={toDatetimeLocal(dayLog[meal]?.Timestamp)}
                      onChange={(e) => {
                        const updatedTimestamp = new Date(
                          e.target.value
                        ).toISOString();
                        updateMeal(meal, {
                          ...dayLog[meal],
                          Timestamp: updatedTimestamp,
                        });
                      }}
                    />
                  </div>

                  <canvas
                    id={`log-save-canvas-${index}`}
                    style={{ transform: "translate(-25%, -25%)" }}
                    className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-pointer-events-none tw-z-[9999]"
                  ></canvas>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DailyMoodLogger;
